/**
 * New area and Edit area (slice 6d spec §8.4, §3; Paper A3): a dialog, a
 * bottom sheet on a phone. New area takes a name, optionally an icon (else
 * Crew picks one from the name) and a first fact for its brief, says who
 * sees an Area, and lands on its page. Edit area renames it, changes its icon and Where it stands, moves
 * it up or down the sidebar, and archives or restores it.
 */
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Field, FieldDescription, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Textarea } from "@crew/ui/components/textarea";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import { useCreateArea, useEditArea, type Area, type AreaEdit } from "./api";
import { areaChanges } from "./area-view";
import { IconPicker } from "./parts";

const LABEL_MAX = 40;
const SUMMARY_MAX = 400;
const FACT_MAX = 400;

/** A dialog on a desktop, a bottom sheet on a phone, with a title and a line under it. */
function Window({
  open,
  onOpenChange,
  title,
  line,
  busy,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  line: string;
  busy: boolean;
  children: (mobile: boolean) => ReactNode;
}) {
  const mobile = useIsMobile();
  const change = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={change} showSwipeHandle>
        <DrawerContent showCloseButton={!busy} className="pb-[env(safe-area-inset-bottom)]">
          <DrawerHeader className="pb-5">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{line}</DrawerDescription>
          </DrawerHeader>
          {open ? children(true) : null}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="sm:max-w-[520px]" showCloseButton={!busy}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{line}</DialogDescription>
        </DialogHeader>
        {open ? children(false) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Save and Cancel: side by side on a desktop, stacked with Save first on a phone. */
function Actions({
  mobile,
  submit,
  onCancel,
  busy,
  extra,
}: {
  mobile: boolean;
  submit: ReactNode;
  onCancel: () => void;
  busy: boolean;
  extra?: ReactNode;
}) {
  const Footer = mobile ? DrawerFooter : DialogFooter;
  const cancel = (
    <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
      Cancel
    </Button>
  );
  return (
    <Footer className={mobile ? undefined : "sm:justify-between"}>
      {mobile ? null : <div>{extra}</div>}
      <div className={mobile ? "contents" : "flex gap-2"}>
        {mobile ? null : cancel}
        {submit}
        {mobile ? cancel : null}
      </div>
      {mobile ? extra : null}
    </Footer>
  );
}

export function NewAreaWindow({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Instead of landing on its page: the Area sheet files the chat in it (spec §8.3). */
  onCreated?: (area: Area) => void;
}) {
  const navigate = useNavigate();
  const create = useCreateArea();
  const change = (next: boolean) => {
    if (!next) create.reset();
    onOpenChange(next);
  };
  return (
    <Window
      open={open}
      onOpenChange={change}
      title="New area"
      line="A part of life or work to keep apart: the gym build, the house, the books."
      busy={create.isPending}
    >
      {(mobile) => (
        <NewAreaForm
          mobile={mobile}
          busy={create.isPending}
          error={create.error}
          onCancel={() => change(false)}
          onCreate={(input) =>
            create.mutate(input, {
              onSuccess: ({ area }) => {
                change(false);
                if (onCreated) onCreated(area);
                // Then Move these in? (spec §7.2), on its page.
                else void navigate(`/areas/${area.slug}?move=1`);
              },
            })
          }
        />
      )}
    </Window>
  );
}

function NewAreaForm({
  mobile,
  busy,
  error,
  onCancel,
  onCreate,
}: {
  mobile: boolean;
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onCreate: (input: { label: string; icon?: string; fact?: string }) => void;
}) {
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState<string | null>(null);
  const [fact, setFact] = useState("");
  const ready = label.trim().length > 0 && !busy;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onCreate({
      label: label.trim(),
      ...(icon ? { icon } : {}),
      ...(fact.trim() ? { fact: fact.trim() } : {}),
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-5" data-new-area>
      <div className={mobile ? "flex flex-col gap-5 px-4" : "flex flex-col gap-5"}>
        <Field>
          <FieldLabel htmlFor="new-area-label">Name</FieldLabel>
          <Input
            id="new-area-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={LABEL_MAX}
            placeholder="Garage gym"
            autoFocus
          />
          <FieldDescription>
            Everyone in your crew sees what's in an area. For something private, use a workspace of
            its own.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel>Icon</FieldLabel>
          <IconPicker value={icon} onChange={setIcon} disabled={busy} />
          <FieldDescription>Optional. Crew picks one from the name.</FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="new-area-fact">What should the crew know first?</FieldLabel>
          <Input
            id="new-area-fact"
            value={fact}
            onChange={(e) => setFact(e.target.value)}
            maxLength={FACT_MAX}
            placeholder="The budget is $3,000. Ask Nadia before anything over $250."
          />
          <FieldDescription>Optional. The crew reads it whenever it works here.</FieldDescription>
        </Field>
        <Problem error={error} />
      </div>
      <Actions
        mobile={mobile}
        busy={busy}
        onCancel={onCancel}
        submit={
          <Button type="submit" disabled={!ready}>
            {busy ? "Making…" : "Make area"}
          </Button>
        }
      />
    </form>
  );
}

type Moves = { index: number; up: boolean; down: boolean };

export function EditAreaWindow({
  area,
  moves,
  open,
  onOpenChange,
}: {
  area: Area;
  /** Its place in the sidebar, and whether it can go up or down (`areaMoves`). */
  moves: Moves;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const edit = useEditArea(area.id);
  const change = (next: boolean) => {
    if (!next) edit.reset();
    onOpenChange(next);
  };
  const save = (input: AreaEdit, close = true) =>
    edit.mutate(input, { onSuccess: () => (close ? change(false) : undefined) });
  return (
    <Window
      open={open}
      onOpenChange={change}
      title="Edit area"
      line="Its name and icon, and where it stands."
      busy={edit.isPending}
    >
      {(mobile) => (
        <EditAreaForm
          area={area}
          mobile={mobile}
          edit={edit}
          moves={moves}
          onCancel={() => change(false)}
          onSave={save}
        />
      )}
    </Window>
  );
}

/** Move up and Move down: the sidebar's order on a phone, where there's no dragging. */
function MoveButtons({
  moves,
  busy,
  onMove,
}: {
  moves: Moves;
  busy: boolean;
  onMove: (position: number) => void;
}) {
  if (!moves.up && !moves.down) return null;
  return (
    <div className="flex gap-2" data-area-move>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!moves.up || busy}
        onClick={() => onMove(moves.index - 1)}
      >
        Move up
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!moves.down || busy}
        onClick={() => onMove(moves.index + 1)}
      >
        Move down
      </Button>
    </div>
  );
}

function EditAreaForm({
  area,
  mobile,
  edit,
  moves,
  onCancel,
  onSave,
}: {
  area: Area;
  mobile: boolean;
  edit: ReturnType<typeof useEditArea>;
  moves: Moves;
  onCancel: () => void;
  onSave: (input: AreaEdit, close?: boolean) => void;
}) {
  const [draft, setDraft] = useState({ label: area.label, icon: area.icon, summary: area.summary });
  const set = (field: keyof typeof draft) => (value: string) =>
    setDraft((d) => ({ ...d, [field]: value }));
  const busy = edit.isPending;
  const changes = areaChanges(area, draft);
  const ready = draft.label.trim().length > 0 && Object.keys(changes).length > 0 && !busy;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onSave(changes);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-5" data-edit-area>
      <div className={mobile ? "flex flex-col gap-5 px-4" : "flex flex-col gap-5"}>
        <Field>
          <FieldLabel htmlFor="edit-area-label">Name</FieldLabel>
          <Input
            id="edit-area-label"
            value={draft.label}
            onChange={(e) => set("label")(e.target.value)}
            maxLength={LABEL_MAX}
          />
        </Field>
        <Field>
          <FieldLabel>Icon</FieldLabel>
          <IconPicker value={draft.icon} onChange={set("icon")} disabled={busy} />
        </Field>
        <Field>
          <FieldLabel htmlFor="edit-area-summary">Where it stands</FieldLabel>
          <Textarea
            id="edit-area-summary"
            value={draft.summary}
            onChange={(e) => set("summary")(e.target.value)}
            maxLength={SUMMARY_MAX}
            rows={3}
            placeholder="The rack arrives Thursday. The bench waits on a measurement."
          />
          <FieldDescription>
            A line or two at the top of the area. The crew keeps it too.
          </FieldDescription>
        </Field>
        <MoveButtons moves={moves} busy={busy} onMove={(position) => onSave({ position }, false)} />
        <Problem error={edit.error} />
      </div>
      <Actions
        mobile={mobile}
        busy={busy}
        onCancel={onCancel}
        extra={<ArchiveButton area={area} busy={busy} onSave={onSave} />}
        submit={
          <Button type="submit" disabled={!ready}>
            {busy ? "Saving…" : "Save"}
          </Button>
        }
      />
    </form>
  );
}

function ArchiveButton({
  area,
  busy,
  onSave,
}: {
  area: Area;
  busy: boolean;
  onSave: (input: AreaEdit) => void;
}) {
  const archived = area.archived_at !== null;
  return (
    <Button
      type="button"
      variant="ghost"
      className="text-destructive"
      disabled={busy}
      onClick={() => onSave({ archived: !archived })}
      data-area-archive
    >
      {archived ? "Restore area" : "Archive area"}
    </Button>
  );
}

function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-meta text-brick">
      {errorHint(error)}
    </p>
  );
}
